/**
 * BottomNav — primary nav for ≤ 767px screens (AC-108 / UX §3.16).
 *
 * Four tabs: Home, Places, Capture (centered, accent-tinted, opens the
 * CaptureSheet), Settings. Hairline top border, generous touch targets,
 * Phosphor icons in `regular` weight. Hidden at md+ where the TopNav handles
 * navigation.
 */
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  House,
  Storefront,
  Plus,
  GearSix,
} from '@phosphor-icons/react';
import { useAppStore } from '../../lib/store';
import { cn } from '../../lib/cn';

interface NavTabProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
}

function NavTab({ to, icon, label, active }: NavTabProps) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-1 py-2',
        'font-sans text-[0.6rem] uppercase tracking-[0.16em]',
        'transition-colors duration-200 ease-out-slow',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent',
        active
          ? 'text-ink-900'
          : 'text-ink-500 hover:text-ink-900',
      )}
    >
      <span aria-hidden>{icon}</span>
      <span>{label}</span>
    </Link>
  );
}

export function BottomNav() {
  const location = useLocation();
  const openCaptureSheet = useAppStore((s) => s.openCaptureSheet);

  // Active matching: keep simple — Home matches `/` and `/home` exactly;
  // Places matches anything under `/places`; Settings matches `/settings`.
  const path = location.pathname;
  const homeActive = path === '/' || path === '/home';
  const placesActive = path.startsWith('/places');
  const settingsActive = path.startsWith('/settings');

  return (
    <nav
      className="md:hidden sticky bottom-0 z-30 bg-bg border-t border-ink-200"
      aria-label="Primary"
      data-testid="bottom-nav"
    >
      <div className="mx-auto max-w-content w-full flex items-stretch">
        <NavTab
          to="/home"
          icon={<House size={22} weight="regular" />}
          label="Home"
          active={homeActive}
        />
        <NavTab
          to="/places"
          icon={<Storefront size={22} weight="regular" />}
          label="Places"
          active={placesActive}
        />
        <button
          type="button"
          onClick={openCaptureSheet}
          className={cn(
            'flex flex-1 flex-col items-center justify-center gap-1 py-2',
            'font-sans text-[0.6rem] uppercase tracking-[0.16em] text-accent',
            'bg-accent-soft/40',
            'transition-colors duration-200 ease-out-slow hover:bg-accent-soft',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent',
          )}
          aria-label="Capture a new entry"
          data-testid="bottom-nav-capture"
        >
          <span aria-hidden>
            <Plus size={22} weight="regular" />
          </span>
          <span>Capture</span>
        </button>
        <NavTab
          to="/settings"
          icon={<GearSix size={22} weight="regular" />}
          label="Settings"
          active={settingsActive}
        />
      </div>
    </nav>
  );
}
